/**
 * RAOC-22 — Canonical `useRovoConfig` hook, ported from
 * `@atlassian/search-page/src/controllers/config/index.tsx` (the search-page
 * `useFetchConfig`).
 *
 * This hook is intended to become the canonical way to fetch the combined
 * Rovo search bootstrap config (1P + 3P + nouns + site metadata + linked
 * sites). It is currently being rolled out behind the
 * `rovo_fps_use_rovo_config` feature gate and is **not yet** the supported
 * replacement for the legacy `useFetchConfig` (also exported from this
 * package) — that hook will only be marked `@deprecated` after rollout
 * completes. See RAOC-22.
 *
 * Also being rolled out behind the `rovo_cas_use_rovo_config` as part of RAOC-23
 * used by downstreams of conversation-assistant-store (Chat, etc)
 *
 * Differences from search-page's `useFetchConfig` body (clean up when removing rovo_fps_use_rovo_config):
 *   - `primaryProduct` is **not** a prop. Primary-product patching moves
 *     out of the hook and into search-page as a post-hook transform
 *     applied at the call site (PR-3). Per plan decision #21.
 *   - Analytics are fully internalised: the hook builds its own
 *     `fireAnalyticsEvent` via `useAnalyticsEvents()` from
 *     `@atlaskit/analytics-next` and its own `logException`. No `analytics`
 *     prop is required.
 *   - VitalStats task names are hard-coded at module scope
 *     (`search-page/combined-config-ux` and
 *     `search-page/combined-config-network`) — owned by this
 *     hook, not caller-supplied. Same rationale for `actionSubject` and the
 *     VitalStats event factories themselves (they live in
 *     `../network/network-task-tracker`, not as a prop). The analytics
 *     taxonomy is part of this hook's contract with the dashboards, not
 *     something each caller redefines. Per RAOC-22 open questions Q1
 *     and Q3 (resolved 2026-05-25).
 *   - Optional `shouldBypassPreloadProvider(preloaded)` callback lets the
 *     caller skip the preload provider for specific sources (search-page
 *     passes `isQuickFindAsyncPreloadEnabled() && preloaded?.source ===
 *     'quick-find'`). Per plan decision #5.
 *   - `preloadDataProvider` is typed as the narrow
 *     `RovoConfigPreloadProvider` (see `./combined-config/preload-types.ts`).
 *     Search-page's wider `SearchPreloadDataProvider` satisfies the narrow
 *     shape structurally.
 *   - The combined-config `parseCombinedConfig` post-processing is removed
 *     from this hook: callers that want it should call `parseCombinedConfig`
 *     directly on the returned `combinedConfigResponse`. This keeps the
 *     hook's return shape stable across the `search_async_ssr` cleanup
 *     boundary (post-cleanup shape per plan decision #7).
 *   - `isNounsAvailable` is **not** returned — per plan decision #8.
 *
 * thirdPartyConfigs{}.userNeedsOAuth is overridded by a global in-tab store
 * that tracks which products/datasources have been OAuth-connected. This is
 * to ensure that all instances of useRovoConfig reflect the same OAuth state.
 * Any usages of onOAuthSuccess will propagate the updated userNeedsOAuth value
 * to all useRovoConfig hook instances. Now when a user oauths successfully from
 * from any downstream of useRovoConfig, all experiences get the connected state (search<>chat etc)
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	fillSsrCombinedConfigCache,
	getCombinedSearchConfig,
	type SearchCombinedConfigPayload,
	seedBaseCombinedConfigCacheEntry,
	updateBaseCombinedConfigCacheEntry,
} from '@atlassian/search-client/config';
import { type DegradationCallback } from '@atlassian/search-client/degradable-promise';
// `TProductEdition`, `TProductDataRegions`, `TRovoFeatures` are intentionally
// imported via inline `import('@atlassian/search-client').T*` expressions
// below (in the `RovoConnectorsConfig` return-type fields) — matching
// search-page's `useFetchConfig` exactly so the two hook return types are
// nominally identical and a `satisfies ReturnType<typeof useFetchConfig>`
// at the call site type-checks. This goes against the AFM "avoid barrels"
// guidance — see RAOC-22 working doc §PR-4 cleanup notes: `@atlassian/search-client`
// should expose these types via a non-barrel subpath that both hooks can
// import.
//
// `TSearchConfigAPIResponse` is kept on the subpath because it is only used
// internally by this hook and is not part of the public return shape, so
// type-identity drift with search-page does not matter for it.
import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';
import { type CacheAnalyticsCallback } from '@atlassian/search-client/persist-refresh';
import { searchLinkedSitesQuery } from '@atlassian/search-client/search-page';
import { type LinkedSite } from '@atlassian/search-client/search-page/types';

import { type NounKeys } from '../../common/constants/types';
import {
	type ForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/types/schemas/types';
import { type ProductKeys3P } from '../../common/ui/configs/types';
import { type FireAnalyticsEvent as FireAnalyticsEventType } from '../../common/utils/analytics/types';
import {
	getFirstCloudId,
	getJiraIssueIndexedForTenant,
	getProductDataRegions,
	getProductEdition,
	getRovoFeatures,
	isRovoEnabledForSite,
} from '../../utils/site-metadata-helpers';
import { adaptCombinedConfigToLegacy } from '../combined-config/adapter';
import { type OnForgeConnectorTypeMappingFn } from '../combined-config/forge';
import {
	type RovoConfigPreloadData,
	type RovoConfigPreloadProvider,
} from '../combined-config/preload-types';
import {
	getThirdPartyTypes,
	map3pResourcesToProductKeys,
} from '../combined-config/third-party-products';
import { fetchCurrentUserAccountId } from '../current-user/fetch-current-user-account-id';
import { onDegradedDependency } from '../events/degraded-dependency';
import { onFailedDependency } from '../events/failed-dependency';
import { logException } from '../events/log-exception';
import { onRequestCacheAccess } from '../events/on-cache-access';
import {
	createVitalStatsNetworkCallbacks,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../network/network-task-tracker';

import { applyOAuthOverrides } from './helpers/applyOAuthOverrides';
import {
	type AccessibleProductsConfig,
	deriveAccessibleProductsConfig,
} from './helpers/deriveAccessibleProductsConfig';
import {
	getOAuthOverridesVersion,
	hasAnyOAuthOverrideForCloud,
	isOAuthConnected,
	markOAuthConnected,
	subscribeToOAuthOverrides,
} from './oauthOverrideStore';

/**
 * Input to the `onOAuthSuccess` callback returned by `useRovoConfig`.
 * Callers (e.g. the search-page OAuth handler) invoke this once an OAuth
 * popup completes for a third-party connector. Pass `datasourceId` for
 * Forge connectors that expose multiple datasources per product.
 */
export type OnOAuthSuccessInput = {
	product: ProductKeys3P | ForgeConnectorProductKey;
	datasourceId?: string;
};

/**
 * Type of the `onOAuthSuccess` callback returned by `useRovoConfig`.
 */
export type OnOAuthSuccessCallback = (input: OnOAuthSuccessInput) => void;

export type FireAnalyticsEvent = FireAnalyticsEventType;

// External callers should import RovoConfigPreloadData / RovoConfigPreloadProvider
// directly from '@atlassian/rovo-connectors-config/hooks/useRovoConfig'
// (via the local aliases below) — re-exports are forbidden by no-barrel-files.
export type RovoConfigPreloadDataType = RovoConfigPreloadData;
export type RovoConfigPreloadProviderType = RovoConfigPreloadProvider;

export type RovoConnectorsConfig = {
	accessibleProducts: string[] | undefined;
	accessibleNouns: NounKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>> | undefined;
	firstCloudId: string;
	intercomHmac: string | undefined;
	isRovoEnabled: boolean | undefined;
	isJiraIssueIndexed: boolean | undefined;
	productEdition: import('@atlassian/search-client').TProductEdition | undefined;
	productDataRegions: import('@atlassian/search-client').TProductDataRegions | undefined;
	rovoFeatures: import('@atlassian/search-client').TRovoFeatures | undefined;
	isConfigLoaded: boolean;
	linkedCloudARIs: string[] | undefined;
	combinedConfigResponse: SearchCombinedConfigPayload | undefined;
	onOAuthSuccess: OnOAuthSuccessCallback;
};

export type UseRovoConfigProps = {
	cloudId?: string;
	aggAbsoluteUrl?: string;
	multiSiteSearchInRovo?: boolean;

	/**
	 * Optional narrow preload provider. If provided and
	 * `shouldBypassPreloadProvider` returns `false` (or is undefined), the
	 * hook will try to seed initial state from the provider before falling
	 * back to a fresh network fetch.
	 */
	preloadDataProvider?: RovoConfigPreloadProvider;

	/**
	 * Documented contract: called synchronously once per cloudId change,
	 * after `preloadDataProvider.getPreloadedSearch(...)` returns; returning
	 * true causes the hook to treat the preload as null and fall through to
	 * a fresh fetch. Per plan decision #5.
	 */
	shouldBypassPreloadProvider?: (preloaded: RovoConfigPreloadData | null) => boolean;

	/**
	 * Optional handler invoked once per Forge connector as its config is
	 * produced, with the connector's `(productKey, typeFilterConfig)`. The host
	 * app decides what to do with it — search-page registers it into its
	 * type-filter mapping cache. Without this handler, Forge connector type
	 * mappings are not surfaced to the host (the Forge connector configs are
	 * still produced).
	 */
	onForgeConnectorTypeMapping?: OnForgeConnectorTypeMappingFn;
};

const EMPTY_RESPONSE: (ProductKeys3P | ForgeConnectorProductKey)[] = [];

/**
 * VitalStats task names emitted by this hook. Owned by `useRovoConfig`
 * (not caller-supplied) — see RAOC-22 open question Q1.
 *
 * TODO RAOC-22 migrate analytics off search-specific naming
 */
const TASK_NAME_UX = 'search-page/combined-config-ux';
const TASK_NAME_NETWORK = 'search-page/combined-config-network';

// TODO RAOC-22 migrate analytics off search-specific naming
const source = 'advancedSearchScreen';

/**
 * React hook which fetches and exposes the Rovo combined search bootstrap
 * config (1P + 3P + nouns + site metadata + linked sites).
 *
 * Intended successor to the legacy `useFetchConfig` exported from
 * `@atlassian/rovo-connectors-config/hooks` — currently being rolled out
 * behind the `rovo_fps_use_rovo_config` feature gate. See module header for
 * the full set of differences and plan references.
 */
export const useRovoConfig = ({
	cloudId: cloudIdProp,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	preloadDataProvider,
	shouldBypassPreloadProvider,
	onForgeConnectorTypeMapping,
}: UseRovoConfigProps): RovoConnectorsConfig => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(payload: FireAnalyticsEventType) => {
			createAnalyticsEvent({
				...payload,
				source: payload.source || source,
				attributes: {
					...payload.attributes?.commonAttributes,
					...payload.attributes?.additionalAttributes,
				},
			}).fire('fabric-elements');
		},
		[createAnalyticsEvent],
	);

	const taskNameUx = TASK_NAME_UX;
	const taskNameNetwork = TASK_NAME_NETWORK;

	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);

	const [combinedThirdPartyResponse, setCombinedThirdPartyResponse] = useState<{
		productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});

	const [linkedCloudARIs, setLinkedCloudARIs] = useState<string[] | undefined>(undefined);

	const [combinedConfigResponse, setCombinedConfigResponse] =
		useState<SearchCombinedConfigPayload>();

	const [accessibleProductsConfig, setAccessibleProductsConfig] =
		useState<AccessibleProductsConfig>({
			nouns: [],
		});

	// Keep a live ref to the latest thirdPartyConfigs so the stable
	// `onOAuthSuccess` callback (created once with no React-state deps)
	// can read the current value without going stale across renders.
	const thirdPartyConfigsRef = useRef(accessibleProductsConfig.thirdPartyConfigs);
	thirdPartyConfigsRef.current = accessibleProductsConfig.thirdPartyConfigs;

	const onOAuthSuccess: OnOAuthSuccessCallback = useCallback(
		({ product, datasourceId }: OnOAuthSuccessInput) => {
			const currentConfigs = thirdPartyConfigsRef.current;
			const productConfig = currentConfigs?.[product];
			if (!productConfig) {
				return;
			}
			if (
				datasourceId !== undefined &&
				!productConfig.datasources?.some((ds) => ds.datasourceId === datasourceId)
			) {
				return;
			}

			let nextConfigs: ThirdPartyConfigsBootstrap = currentConfigs;
			if (datasourceId === undefined) {
				if (productConfig.userNeedsOAuth !== false) {
					nextConfigs = {
						...currentConfigs,
						[product]: { ...productConfig, userNeedsOAuth: false },
					};
				}
			} else {
				const datasources = productConfig.datasources ?? [];
				const targetIdx = datasources.findIndex((ds) => ds.datasourceId === datasourceId);
				if (targetIdx >= 0 && datasources[targetIdx].userNeedsOAuth !== false) {
					const nextDatasources = datasources.map((ds, i) =>
						i === targetIdx ? { ...ds, userNeedsOAuth: false } : ds,
					);
					nextConfigs = {
						...currentConfigs,
						[product]: { ...productConfig, datasources: nextDatasources },
					};
				}
			}

			thirdPartyConfigsRef.current = nextConfigs;
			setAccessibleProductsConfig((prev) => ({ ...prev, thirdPartyConfigs: nextConfigs }));

			// Record the connection in the shared, in-tab OAuth-override store so
			// that EVERY other `useRovoConfig` instance for this cloudId (not just
			// this one) reactively re-renders and overlays `userNeedsOAuth: false`.
			// This is what gives `useRovoConfig` its singleton-like OAuth behaviour
			// across independent component subtrees. Keyed on the same cloudId used
			// for the persisted cache below.
			if (cloudIdProp) {
				markOAuthConnected(cloudIdProp, product, datasourceId);
			}

			if (cloudIdProp) {
				const thirdPartyTypes = getThirdPartyTypes();
				updateBaseCombinedConfigCacheEntry(cloudIdProp, (config) => ({
					...config,
					thirdPartyProducts: config.thirdPartyProducts?.map((entry) => {
						if (datasourceId) {
							if (entry.datasourceId === datasourceId) {
								return { ...entry, isUserOAuthed: true };
							}
						} else if (thirdPartyTypes[entry.name] === product) {
							return { ...entry, isUserOAuthed: true };
						}
						return entry;
					}),
				}));
			}
		},
		[cloudIdProp],
	);

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	// Rovo Multiple Site Search - Store linked site ARIs in state
	useEffect(() => {
		const cloudId = cloudIdProp || firstCloudId;
		// `isCancelled` guards against a stale `searchLinkedSitesQuery` promise
		// resolving after the effect has been re-run (e.g. when `cloudIdProp` or
		// `firstCloudId` changes). Without this, a slower in-flight request could
		// overwrite a newer, correct linkedCloudARIs value. Set in the cleanup
		// function returned below and checked before every state update inside
		// `.then()` / `.catch()`.
		let isCancelled = false;
		if (cloudId && multiSiteSearchInRovo) {
			searchLinkedSitesQuery({
				variables: { id: `ari:cloud:confluence::site/${cloudId}` },
				aggAbsoluteUrl,
			})
				// The shape returned by `searchLinkedSitesQuery` is not exported by
				// `@atlassian/search-client/search-page`, so we narrow it locally:
				// it's a GraphQL response keyed by the operation name with an
				// `edges`/`node`/`id` chain. Use `unknown` + a structural cast at the
				// access site instead of `any` so subsequent property access stays
				// type-checked. Long-term fix is to export a type from search-client
				// — tracked alongside the other search-client type-promotion follow-ups
				// in the RAOC-22 work doc.
				.then((response: unknown) => {
					const edges =
						(
							response as
								| {
										data?: {
											collabContext_workspaceIsConnectedToWorkspace?: {
												edges?: LinkedSite[];
											};
										};
								  }
								| undefined
						)?.data?.collabContext_workspaceIsConnectedToWorkspace?.edges ?? [];
					const linkedARIs = edges.map((edge: LinkedSite) => edge.node.id);

					const currentCloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
					const currentIndex = linkedARIs.findIndex((ari: string) => ari === currentCloudIdARI);

					if (currentIndex > 0) {
						linkedARIs.splice(currentIndex, 1);
						linkedARIs.unshift(currentCloudIdARI);
					}

					if (isCancelled) {
						return;
					}
					setLinkedCloudARIs(linkedARIs);
				})
				.catch((error: Error) => {
					if (isCancelled) {
						return;
					}
					logException(error);
				});
		}
		return () => {
			isCancelled = true;
		};
	}, [cloudIdProp, firstCloudId, multiSiteSearchInRovo, aggAbsoluteUrl]);

	useEffect(() => {
		if (!firstPartyResponse) {
			if (!cloudIdProp) {
				return;
			}

			if (combinedThirdPartyResponse.productKeys) {
				return;
			}

			const combinedConfigTTL = 1000 * 60 * 1; // 1 minute

			const fetchCombinedConfig = () => {
				const uxTaskId = crypto.randomUUID();
				const taskStartAt = performance.now();

				const baseUXVitalStatsParams = {
					taskId: uxTaskId,
					task: taskNameUx,
				};

				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-combined-config',
							reason,
							source,
						}) as FireAnalyticsEvent,
					);
				};

				// NOTE: `CacheAnalyticsCallback` is strictly typed as
				// `(state) => Promise<void>` in `@atlassian/search-client/persist-refresh`,
				// so the `async` keyword is structurally required even though the body
				// is synchronous. (Earlier review feedback suggested dropping `async`
				// — that turns out to be incorrect against the current type.) The
				// long-term fix is to widen `CacheAnalyticsCallback` to also accept
				// `void`, tracked as a search-client-tightening follow-up alongside
				// the other type-promotion items in the RAOC-22 work doc.
				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-combined-config',
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source,
						}) as FireAnalyticsEvent,
					);
				};

				const configNetworkTaskId = crypto.randomUUID();
				const networkCallbacks = createVitalStatsNetworkCallbacks(
					configNetworkTaskId,
					taskNameNetwork,
					// `createVitalStatsNetworkCallbacks` types its sink as
					// `(event: unknown) => void` to match the factory return
					// shape; our caller sink is typed `(event: FireAnalyticsEvent) => void`.
					// Adapt at the call site — narrow cast is safe because the
					// values come from `onTaskStart` / `onTaskSuccess` / `onTaskFail`
					// in `../network/network-task-tracker`, owned by this package.
					(event: unknown) => fireAnalyticsEvent(event as FireAnalyticsEvent),
					taskStartAt,
					{ uxTaskId },
				);

				const searchConfigParams = {
					cloudId: cloudIdProp,
					aggAbsoluteUrl,
					degradationCallback,
					cacheAnalyticsCallback,
					expiration: combinedConfigTTL,
					returnExpired: true,
					onlyRefreshIfExpired: true,
					networkCallbacks,
				};

				fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams) as FireAnalyticsEvent);

				getCombinedSearchConfig(searchConfigParams)
					.then((configuration: SearchCombinedConfigPayload) => {
						setCombinedConfigResponse(configuration);

						// RAOC: fill the AAID-scoped SSR cache key so the next
						// server-side render can hydrate `window.__PRELOADED_SEARCH__`.
						// The hook no longer receives a `userId` prop, so the account
						// id is resolved asynchronously via `/gateway/api/me` (owned by
						// this package) and passed to `fillSsrCombinedConfigCache`.
						if (cloudIdProp) {
							void fetchCurrentUserAccountId()
								.then((accountId) =>
									fillSsrCombinedConfigCache({
										accountId,
										cloudId: cloudIdProp,
										config: configuration,
										expiration: combinedConfigTTL,
									}),
								)
								.catch((fillError: unknown) => {
									logException(fillError);
								});
						}

						const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
							configuration,
							cloudIdProp,
						);
						const mappedThirdPartyResponse = map3pResourcesToProductKeys(
							thirdPartyConfig,
							onForgeConnectorTypeMapping,
						);
						setFirstPartyResponse(firstPartyConfig);
						setCombinedThirdPartyResponse(mappedThirdPartyResponse);

						// Derive and publish the accessible products synchronously here, in the
						// same commit that sets the config responses, rather than deferring to
						// the products effect below. Everything the derivation needs is already
						// available, so this removes an extra render → commit round-trip before
						// the results list can mount. The products effect remains as the
						// preload/SSR-path authority and an idempotent safety net.
						setAccessibleProductsConfig(
							deriveAccessibleProductsConfig(
								firstPartyConfig,
								mappedThirdPartyResponse,
								cloudIdProp,
							),
						);
						setIsConfigLoaded(true);

						fireAnalyticsEvent(
							onTaskSuccess({
								...baseUXVitalStatsParams,
								moreContext: {
									timeTaken: performance.now() - taskStartAt,
								},
							}) as FireAnalyticsEvent,
						);
					})
					.catch((error: Error) => {
						logException(error);

						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-combined-config',
								source,
							}) as FireAnalyticsEvent,
						);

						fireAnalyticsEvent(
							onTaskFail({
								...baseUXVitalStatsParams,
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
								},
							}) as FireAnalyticsEvent,
						);

						setFirstPartyResponse({
							resources: [],
							intercomHmac: '',
							siteMetadata: [],
						});
						setCombinedThirdPartyResponse({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});
					});
			};

			// Read the preload provider at most once per effect run. A stateful
			// provider (e.g. one that clears the entry on first access) would
			// otherwise return null on the second read. Per plan decision #5,
			// `shouldBypassPreloadProvider` receives the cached value below.
			let cachedPreloadedSearch: RovoConfigPreloadData | null = null;
			let hasReadPreload = false;
			const readPreloadedSearch = (): RovoConfigPreloadData | null => {
				if (!hasReadPreload) {
					hasReadPreload = true;
					try {
						cachedPreloadedSearch =
							preloadDataProvider?.getPreloadedSearch({ cloudId: cloudIdProp }) ?? null;
					} catch {
						// Reading the preload provider must never throw — be defensive.
						cachedPreloadedSearch = null;
					}
				}
				return cachedPreloadedSearch;
			};

			// Optional bypass — see plan decision #5
			let shouldBypass = false;
			if (shouldBypassPreloadProvider) {
				shouldBypass = shouldBypassPreloadProvider(readPreloadedSearch());
			}

			if (preloadDataProvider && !shouldBypass) {
				try {
					const preloadedSearch = readPreloadedSearch();
					if (preloadedSearch && preloadedSearch.config) {
						const preloadUxTaskId = crypto.randomUUID();
						const preloadTaskStartAt = performance.now();
						const preloadMoreContext = {
							isSSREvent: true,
							isConfigFromCache: Boolean(preloadedSearch.isConfigFromCache),
						};
						const preloadBaseUXVitalStatsParams = {
							taskId: preloadUxTaskId,
							task: taskNameUx,
							moreContext: preloadMoreContext,
						};
						fireAnalyticsEvent(onTaskStart(preloadBaseUXVitalStatsParams) as FireAnalyticsEvent);

						try {
							setCombinedConfigResponse(preloadedSearch.config);
							const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
								preloadedSearch.config,
								cloudIdProp,
							);
							setFirstPartyResponse(firstPartyConfig);
							setCombinedThirdPartyResponse(
								map3pResourcesToProductKeys(thirdPartyConfig, onForgeConnectorTypeMapping),
							);
							const preloadedConfig = preloadedSearch.config;
							if (!preloadedSearch.isConfigFromCache && preloadedConfig && cloudIdProp) {
								void seedBaseCombinedConfigCacheEntry({
									cloudId: cloudIdProp,
									config: preloadedConfig,
									expiration: combinedConfigTTL,
								}).catch((seedError: unknown) => {
									logException(seedError);
								});

								void fetchCurrentUserAccountId()
									.then((accountId) =>
										fillSsrCombinedConfigCache({
											accountId,
											cloudId: cloudIdProp,
											config: preloadedConfig,
											expiration: combinedConfigTTL,
										}),
									)
									.catch((fillError: unknown) => {
										logException(fillError);
									});
							}
							fireAnalyticsEvent(
								onTaskSuccess({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}) as FireAnalyticsEvent,
							);
							return;
						} catch (error: unknown) {
							logException(error);
							fireAnalyticsEvent(
								onTaskFail({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										error: error instanceof Error ? error.name : 'unknown',
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}) as FireAnalyticsEvent,
							);
							fetchCombinedConfig();
							return;
						}
					}
					if (preloadedSearch && !preloadedSearch.config) {
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-config-ssr',
								source,
								moreContext: {
									cacheFailReason: preloadedSearch.cacheFailReason ?? 'none',
									configFailReason: preloadedSearch.configFailReason ?? 'none',
									preloadPayloadWithoutConfig: true,
								},
							}) as FireAnalyticsEvent,
						);
					}
					fetchCombinedConfig();
				} catch (error: unknown) {
					logException(error);
					fetchCombinedConfig();
				}
				return;
			}

			fetchCombinedConfig();
		}
	}, [
		firstPartyResponse,
		combinedThirdPartyResponse.productKeys,
		cloudIdProp,
		aggAbsoluteUrl,
		fireAnalyticsEvent,
		preloadDataProvider,
		shouldBypassPreloadProvider,
		taskNameUx,
		taskNameNetwork,
		onForgeConnectorTypeMapping,
	]);

	useEffect(() => {
		if (
			combinedThirdPartyResponse.productKeys &&
			firstPartyResponse &&
			!accessibleProductsConfig.products
		) {
			// On the client fetch path the accessible products are now derived and
			// published synchronously in the config-resolve handler above, so the
			// `!accessibleProductsConfig.products` guard means this effect no longer
			// runs for that navigation. It is retained as the authority for the
			// preload/SSR path — which sets the responses without going through that
			// handler — and as an idempotent safety net, sharing the same pure
			// derivation helper so behaviour is identical on both paths.
			setAccessibleProductsConfig(
				deriveAccessibleProductsConfig(firstPartyResponse, combinedThirdPartyResponse, cloudIdProp),
			);
			setIsConfigLoaded(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `setAccessibleProductsConfig`
		// is a useState setter with a stable identity guaranteed by React; including it in the
		// dep array would be misleading. The fields that genuinely drive this effect are the
		// three values below.
	}, [
		combinedThirdPartyResponse,
		firstPartyResponse,
		cloudIdProp,
		accessibleProductsConfig.products,
	]);

	const accessibleProducts = accessibleProductsConfig.products;

	const isRovoEnabled = isRovoEnabledForSite(firstPartyResponse, cloudIdProp);
	const isJiraIssueIndexed = getJiraIssueIndexedForTenant(firstPartyResponse, cloudIdProp);
	const productEdition = getProductEdition(firstPartyResponse, cloudIdProp);
	const productDataRegions = getProductDataRegions(firstPartyResponse, cloudIdProp);
	const rovoFeatures = getRovoFeatures(firstPartyResponse, cloudIdProp);

	// Subscribe this instance to the shared in-tab OAuth-override store. Any
	// `onOAuthSuccess` (from THIS or ANY OTHER `useRovoConfig` instance) bumps
	// the version, re-rendering every subscriber so the overlay below recomputes.
	const oauthOverridesVersion = useSyncExternalStore(
		subscribeToOAuthOverrides,
		getOAuthOverridesVersion,
		// Server snapshot: no overrides exist during SSR — return a constant 0
		// so the server-side contract is immune to any module-level state that
		// might be non-zero in a long-lived SSR process.
		() => 0,
	);

	// Overlay the shared OAuth overrides onto the derived configs so a connection
	// performed by any instance is reflected here as `userNeedsOAuth: false`.
	const thirdPartyConfigsWithOverrides = useMemo(() => {
		const base = accessibleProductsConfig.thirdPartyConfigs;

		// SSR guard: hasAnyOAuthOverrideForCloud reads from module state, which is not guaranteed to be zero in a long-lived SSR process. If it is non-zero, the server-side contract is violated and the server-rendered markup will mismatch the client render. Return the base configs unmodified during SSR so the server-side contract is preserved.
		if (typeof window === 'undefined') return base;

		if (!cloudIdProp || !hasAnyOAuthOverrideForCloud(cloudIdProp)) {
			return base;
		}
		return applyOAuthOverrides(base, (product, datasourceId) =>
			isOAuthConnected(cloudIdProp, product, datasourceId),
		);
		// `oauthOverridesVersion` is the reactive trigger: it changes whenever the
		// shared store mutates, which is precisely when the overlay must recompute.
		// It acts here as a cache-busting key for the memoization
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessibleProductsConfig.thirdPartyConfigs, cloudIdProp, oauthOverridesVersion]);

	return useMemo(() => {
		return {
			accessibleProducts,
			accessibleNouns: accessibleProductsConfig.nouns,
			thirdPartyConfigs: thirdPartyConfigsWithOverrides,
			firstCloudId,
			intercomHmac: firstPartyResponse?.intercomHmac,
			isRovoEnabled,
			isJiraIssueIndexed,
			productEdition,
			productDataRegions,
			rovoFeatures,
			isConfigLoaded,
			linkedCloudARIs,
			// TODO RAOC-22 clean up when removing FG('rovo_fps_use_rovo_config') and the search-page `useFetchConfig` fallback logic that relies on this field being present in the return type as this will no longer be used
			combinedConfigResponse,
			onOAuthSuccess,
		};
	}, [
		accessibleProducts,
		accessibleProductsConfig.nouns,
		thirdPartyConfigsWithOverrides,
		firstCloudId,
		firstPartyResponse?.intercomHmac,
		isRovoEnabled,
		isJiraIssueIndexed,
		productEdition,
		productDataRegions,
		rovoFeatures,
		isConfigLoaded,
		linkedCloudARIs,
		combinedConfigResponse,
		onOAuthSuccess,
	]);
};
